"use client";

import { FC, useState, useEffect } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import Tooltip from "@mui/material/Tooltip";
import ArrowLeft from "@mui/icons-material/ArrowLeft";
import ArrowRight from "@mui/icons-material/ArrowRight";

import { useStyles } from "./styles";
import { GenerationPageResponse } from "@/types/thread";
import { useAppSelector } from "@/redux/store";
import { getNextGenSlug, getPrevGenSlug, getHighlightedImage } from "./helpers";
import { getDisplayedTimeAgo } from "@/utils/date";
import HighlightedImage from "../HighlightedImage";
import UserDetails from "@/components/profile/UserDetails";
import ShareButton from "@/components/social/share/ShareButton";
import ReportButton from "@/components/idea/Generation/ReportButton";

interface GenSummaryProps {
  data: GenerationPageResponse;
}

const GenSummary: FC<GenSummaryProps> = ({ data }) => {
  const pathname = usePathname() as string;
  const searchParams = useSearchParams();
  const gensWithSlugs = data.additionalGenerations.filter(
    (gen) => gen.slug !== null
  );
  const showArrows = gensWithSlugs.length > 0;
  const { classes } = useStyles({ showArrows });
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");

  const highlightedImage = searchParams?.get("highlighted") || null;
  const sortedImages = [...data.images].sort((a, b) => a.id - b.id);
  const [selectedIndex, setSelectedIndex] = useState(
    getHighlightedImage(highlightedImage, sortedImages, isSubscribed)
  );
  const [showFullDescription, setShowFullDescription] = useState(false);
  const displayedDescription = showFullDescription
    ? data.prompt || ""
    : `${data.prompt?.slice(0, 317)}${
        data.prompt?.length && data.prompt.length > 317 ? "..." : ""
      }`;

  const nextGenSlug = getNextGenSlug(gensWithSlugs, data.createdAt);
  const prevGenSlug = getPrevGenSlug(gensWithSlugs, data.createdAt);

  const toggleShowFullDescription = () => {
    setShowFullDescription((prevValue) => !prevValue);
  };

  useEffect(() => {
    const newHighlightedIndex = getHighlightedImage(
      highlightedImage,
      sortedImages,
      isSubscribed
    );
    if (newHighlightedIndex !== selectedIndex) {
      setSelectedIndex(newHighlightedIndex);
    }
  }, [highlightedImage]);

  return (
    <div className={classes.mainContent}>
      <div className={classes.wrapper}>
        <div className={classes.contentInner}>
          <div className={classes.imageViewer}>
            <HighlightedImage
              images={sortedImages}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
            />
          </div>
          <div className={classes.genDetails}>
            <h1 className={classes.prompt}>{data.title}</h1>
            <div className={classes.genDetailsMain}>
              <div>
                <UserDetails
                  username={data.username}
                  avatarUrl={data.avatarUrl}
                  subscriptionType={data.subscriptionType}
                  showAvatar
                />
                <p className={classes.createdAt}>
                  {getDisplayedTimeAgo(data.createdAt)}
                </p>
              </div>
              <div className={classes.genControls}>
                <ShareButton />
                <ReportButton
                  className=""
                  url={`${process.env.NEXT_PUBLIC_BASE_URL}${pathname}`}
                  generationUrls={sortedImages.map((image) => image.url)}
                />
              </div>
            </div>
            {data.prompt && (
              <p
                className={classes.description}
                onClick={toggleShowFullDescription}
                style={{
                  cursor: data.prompt.length > 317 ? "pointer" : "auto",
                }}
              >{`"${displayedDescription}"`}</p>
            )}
            <div className={classes.genNavControls}>
              <Tooltip title="Previous Generation" arrow>
                <Link
                  href={`/gen/${prevGenSlug}`}
                  className={classes.arrowGroup}
                >
                  <ArrowLeft className={classes.arrowIconButton} />
                  <p className={classes.arrowText}>PREV</p>
                </Link>
              </Tooltip>
              <Tooltip title="Next Generation" arrow>
                <Link
                  href={`/gen/${nextGenSlug}`}
                  className={classes.arrowGroup}
                >
                  <p className={classes.arrowText}>NEXT</p>
                  <ArrowRight className={classes.arrowIconButton} />
                </Link>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenSummary;
